<script setup>
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useDisplay } from "vuetify"
import { useModalStore } from "@/stores/ModalStore"
import { useTagsStore } from "@/stores/TagsStore"
import STag from "@/components/chips/STag.vue"

/**
 * Add / edit tag dialog (opened via modalStore.openModal('editTag', '', false, params)).
 * params: { tag?: existing tag to edit, name?: prefilled name, type?: 'events' | 'clients' preselected for new tag }
 * Resolves with the saved tag, or null when cancelled / deleted.
 */
const THIS_MODAL_NAME = 'editTag'
const PALETTE = ['#A9D4F5', '#7EE1C7', '#B3D88A', '#E8DC7A', '#C7BBF2', '#E5BCEB'] // uppercase: compared with toUpperCase()

const modalStore = useModalStore()
const tagsStore = useTagsStore()
const { t } = useI18n()
const { mobile } = useDisplay()

const params = modalStore.modalParams[THIS_MODAL_NAME] ?? {}
const isNew = !params.tag?.id

const form = ref({
  id: params.tag?.id ?? null,
  name: params.tag?.name ?? params.name ?? '',
  color: params.tag?.color ?? PALETTE[0],
  forEvents: params.tag?.forEvents ?? params.type === 'events',
  forClients: params.tag?.forClients ?? params.type === 'clients',
})

const isSaving = ref(false)

// ========================================
// Custom color
// ========================================
// Color picker writes straight to form.color, so preview and hex input update live
const isCustomColor = computed(() => !PALETTE.includes(form.value.color.toUpperCase()))

// Hex input: shows current color; typing a full #RRGGBB (with or without #) applies it
const HEX_PATTERN = /^#[0-9A-F]{6}$/i
const hexText = ref(form.value.color.toUpperCase())

watch(() => form.value.color, color => { hexText.value = color.toUpperCase() })

const onHexInput = value => {
  const hex = value?.startsWith('#') ? value : `#${value ?? ''}`
  if (HEX_PATTERN.test(hex)) form.value.color = hex.toUpperCase()
}

// Incomplete value is dropped, field shows the current color again
const resetHexText = () => { hexText.value = form.value.color.toUpperCase() }

// ========================================
// Actions
// ========================================
const canSave = computed(() => form.value.name.trim() && (form.value.forEvents || form.value.forClients))

const close = () => modalStore.closeModal(THIS_MODAL_NAME)

const save = async () => {
  isSaving.value = true
  try {
    const tag = await tagsStore.saveTag({ ...form.value, name: form.value.name.trim() })
    if (tag) modalStore.closeModal(THIS_MODAL_NAME, tag)
  } finally {
    isSaving.value = false
  }
}

const remove = async () => {
  const confirmed = await modalStore.openConfirmationModal(t('delete_tag_confirm', { name: params.tag.name }), t('delete_tag'))
  if (!confirmed) return

  isSaving.value = true
  try {
    if (await tagsStore.deleteTag(params.tag)) close()
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="modalStore.modals.editTag"
    max-width="440"
    persistent
    @update:model-value="close"
  >
    <VCard>
      <VCardTitle class="d-flex justify-space-between align-center pa-4">
        {{ isNew ? $t('new_tag') : $t('edit_tag') }}
        <VIcon
          icon="ri-close-line"
          :disabled="isSaving"
          @click="close"
        />
      </VCardTitle>

      <VCardText class="d-flex flex-column ga-4 mt-0 pt-2">
        <VTextField
          v-model="form.name"
          autofocus
          persistent-placeholder
          :label="$t('tag_name')"
          density="compact"
          color="secondary"
          maxlength="50"
          hide-details
          class="flex-grow-0"
          :disabled="isSaving"
          @keydown.enter="canSave && save()"
        />

        <div>
          <div class="text-body-2 mb-2">
            {{ $t('tag_color') }}
          </div>
          <!-- Smaller gaps on phones, so presets + separator + picker fit in one row -->
          <div class="d-flex flex-wrap align-center ga-1 ga-sm-2">
            <VBtn
              v-for="color in PALETTE"
              :key="color"
              :color="color"
              :icon="form.color.toUpperCase() === color ? 'ri-check-line' : undefined"
              size="28"
              variant="flat"
              class="color-swatch"
              :disabled="isSaving"
              @click="form.color = color"
            />
            <!-- Separates predefined colors from the custom color picker; on xs a line break instead -->
            <VDivider
              vertical
              class="align-self-stretch mx-1 d-none d-sm-flex"
            />
            <div class="w-100 d-sm-none" />
            <VMenu :close-on-content-click="false">
              <template #activator="{ props: menuProps }">
                <VBtn
                  v-bind="menuProps"
                  :color="isCustomColor ? form.color : undefined"
                  :icon="isCustomColor ? 'ri-check-line' : 'ri-palette-line'"
                  :variant="isCustomColor ? 'flat' : 'outlined'"
                  size="28"
                  class="color-swatch"
                  :disabled="isSaving"
                />
              </template>
              <template #default="{ isActive }">
                <div class="d-flex flex-column">
                  <VColorPicker
                    v-model="form.color"
                    elevation="5"
                    mode="hex"
                    hide-inputs
                  />
                  <VBtn
                    variant="flat"
                    color="primary"
                    size="small"
                    class="w-100 rounded-0 rounded-b"
                    prepend-icon="ri-close-large-line"
                    @click="isActive.value = false"
                  >
                    {{ $t('close') }}
                  </VBtn>
                </div>
              </template>
            </VMenu>
            <VTextField
              v-model="hexText"
              density="compact"
              color="secondary"
              maxlength="7"
              hide-details
              class="hex-input ms-sm-auto flex-grow-0"
              style="inline-size: 100px;"
              :disabled="isSaving"
              @update:model-value="onHexInput"
              @blur="resetHexText"
            />
          </div>
        </div>

        <div>
          <div class="text-body-2 mb-2">
            {{ $t('tag_available_for') }}
          </div>
          <VRow dense>
            <VCol cols="6">
              <VSheet
                border
                rounded
                class="px-2"
              >
                <VCheckbox
                  v-model="form.forClients"
                  :disabled="isSaving"
                  hide-details
                  density="compact"
                >
                  <template #label>
                    <VIcon
                      icon="ri-user-fill"
                      size="16"
                      class="me-1"
                    />
                    {{ $t('tag_for_clients') }}
                  </template>
                </VCheckbox>
              </VSheet>
            </VCol>
            <VCol cols="6">
              <VSheet
                border
                rounded
                class="px-2"
              >
                <VCheckbox
                  v-model="form.forEvents"
                  :disabled="isSaving"
                  hide-details
                  density="compact"
                >
                  <template #label>
                    <VIcon
                      icon="ri-calendar-fill"
                      size="16"
                      class="me-1"
                    />
                    {{ $t('visits') }}
                  </template>
                </VCheckbox>
              </VSheet>
            </VCol>
          </VRow>
        </div>

        <VSheet
          color="surface-intensive"
          rounded
          class="d-flex align-center ga-3 pa-3"
        >
          <span class="text-body-2 text-medium-emphasis">{{ $t('preview') }}</span>
          <STag
            :text="form.name || $t('tag_name')"
            :color="form.color"
          />
        </VSheet>
      </VCardText>

      <VCardActions class="pa-4 pt-0">
        <VBtn
          variant="outlined"
          color="primary"
          :disabled="isSaving"
          @click="close"
        >
          {{ $t('cancel') }}
        </VBtn>
        <VBtn
          v-if="!isNew"
          color="error"
          prepend-icon="ri-delete-bin-line"
          :disabled="isSaving"
          @click="remove"
        >
          {{ $t('delete_tag') }}
        </VBtn>
        <VSpacer />
        <VBtn
          variant="flat"
          color="green"
          :loading="isSaving"
          :disabled="!canSave"
          @click="save"
        >
          {{ $t('save') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
/* Narrower inner padding: widest value "#CCCCCC" fits in 100px and the input stays in the colors row */
.hex-input :deep(.v-field) {
  --v-field-padding-start: 12px;
  --v-field-padding-end: 12px;
}

/* Swatches always show the exact color: no darkening overlay on hover, focus or open menu */
.color-swatch :deep(.v-btn__overlay) {
  opacity: 0 !important;
}
</style>
